import { Controller } from "@hotwired/stimulus"

function loadMapLibreScript(src) {
  if (!src) return Promise.reject(new Error("MapLibre GL SDK URL is empty or undefined"))
  if (window.maplibregl) return Promise.resolve(window.maplibregl)
  return new Promise((resolve, reject) => {
    let script = document.querySelector('script[data-maplibre]')
    if (!script) {
      script = document.createElement('script')
      script.setAttribute('data-maplibre', '')
      script.src = src
      document.head.appendChild(script)
    }
    if (window.maplibregl) { resolve(window.maplibregl); return }
    script.addEventListener('load', () => resolve(window.maplibregl), { once: true })
    script.addEventListener('error', reject, { once: true })
  })
}

export default class extends Controller {
  static targets = ["canvas"]
  static values = {
    markers:     { type: Array,  default: [] },
    center:      { type: Array,  default: [78.9629, 20.5937] },
    maplibreUrl: String,
  }

  async connect() {
    try {
      await loadMapLibreScript(this.maplibreUrlValue)
      this.init()
    } catch (e) {
      console.error("MapLibre load failed", e)
    }
  }

  disconnect() {
    if (this._themeObserver) this._themeObserver.disconnect()
    if (this._popups) this._popups.forEach((p) => p.remove())
    if (this.map) this.map.remove()
  }

  isDark() {
    return document.documentElement.classList.contains("dark") || document.body.classList.contains("dark")
  }

  tileUrl() {
    return this.isDark()
      ? "https://basemaps.cartocdn.com/rastertiles/dark_all/{z}/{x}/{y}{r}.png"
      : "https://basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}{r}.png"
  }

  init() {
    const ml = window.maplibregl
    const el = this.hasCanvasTarget ? this.canvasTarget : this.element
    if (!el.id) el.id = "search-map-" + Math.random().toString(36).slice(2, 9)

    this.map = new ml.Map({
      container: el.id,
      style: {
        version: 8,
        sources: { tiles: { type: "raster", tiles: [this.tileUrl()], tileSize: 256, attribution: "© OpenStreetMap, © CARTO" } },
        layers: [{ id: "tiles", type: "raster", source: "tiles", minzoom: 0, maxzoom: 20 }],
      },
      center: this.centerValue,
      zoom: 11,
      attributionControl: false,
    })

    this.map.addControl(new ml.NavigationControl({ showCompass: false }), "top-right")
    this.map.on("load", () => {
      this.map.resize()
      this.addMarkers()
    })

    this._themeObserver = new MutationObserver(() => {
      const src = this.map && this.map.getSource("tiles")
      if (src && src.setTiles) src.setTiles([this.tileUrl()])
    })
    this._themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] })
  }

  addMarkers() {
    const ml = window.maplibregl
    this._popups = []
    const markers = this.markersValue || []
    if (!markers.length) return

    const bounds = new ml.LngLatBounds()

    markers.forEach((m) => {
      if (typeof m.lat !== "number" || typeof m.lng !== "number") return
      const isHospital = m.type === "hospital"
      const color = isHospital ? "#2563eb" : "#0ea5a4"

      const dot = document.createElement("div")
      dot.style.cssText = `width:18px;height:18px;border-radius:9999px;background:${color};border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35);cursor:pointer`

      const popup = new ml.Popup({ offset: 16, closeButton: false }).setHTML(
        `<a href="${m.url}" style="display:block;min-width:160px;text-decoration:none;color:#0f172a;font-family:sans-serif">
           <span style="display:inline-block;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.04em;color:${color}">${isHospital ? (window.MediFyrI18n?.t?.map_pin_hospital || "Hospital") : (window.MediFyrI18n?.t?.map_pin_doctor || "Doctor")}</span>
           <strong style="display:block;font-size:13px;line-height:1.3;margin:2px 0">${this.escape(m.name)}</strong>
           ${m.sub ? `<span style="font-size:11px;color:#64748b">${this.escape(m.sub)}</span>` : ""}
           <span style="display:block;margin-top:6px;font-size:11px;font-weight:700;color:#2563eb">${window.MediFyrI18n?.t?.view_profile || "View profile →"}</span>
         </a>`
      )
      this._popups.push(popup)

      new ml.Marker({ element: dot }).setLngLat([m.lng, m.lat]).setPopup(popup).addTo(this.map)
      bounds.extend([m.lng, m.lat])
    })

    if (!bounds.isEmpty()) {
      this.map.fitBounds(bounds, { padding: 50, maxZoom: 14, duration: 0 })
    }
  }

  escape(s) {
    return String(s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
  }
}
